import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { FocusEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Notice } from '#ynt-design-system/components/notice/notice.tsx'
import type { NoticeAction, NoticeType } from '#ynt-design-system/components/notice/notice.tsx'
import { useEscapeFallback } from '#hooks/use-escape-key.ts'
import { useExitTransition } from '#hooks/use-exit-transition.ts'

export const NOTICE_DURATION = 6000
export const NOTICE_ACTION_DURATION = 8000

interface NoticeBase {
  type?: NoticeType
  message: ReactNode
}

export type NoticeOptions = NoticeBase &
  ({ undo?: () => void; action?: never } | { action?: NoticeAction; undo?: never })

export interface NoticeApi {
  /** Replaces whatever notice is showing. */
  show: (options: NoticeOptions) => void
  dismiss: () => void
}

interface Entry {
  id: number
  options: NoticeOptions
}

const NoticeContext = createContext<NoticeApi | null>(null)

/** Errors and loading stay until dismissed or replaced. */
function durationOf({ type, undo, action }: NoticeOptions): number | undefined {
  if (type === 'error' || type === 'loading') return undefined
  return undo || action ? NOTICE_ACTION_DURATION : NOTICE_DURATION
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || !!target.closest('input, textarea, select'))
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  // Mounted at the app root, so a namespace still loading must not suspend the whole app.
  const { t } = useTranslation('general', { useSuspense: false })
  const [entry, setEntry] = useState<Entry | null>(null)
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const remaining = useRef<{ id: number; ms?: number }>({ id: 0 })
  const nextId = useRef(0)
  const exit = useExitTransition(open)

  const show = useCallback((options: NoticeOptions) => {
    nextId.current += 1
    remaining.current = { id: nextId.current, ms: durationOf(options) }
    setEntry({ id: nextId.current, options })
    setOpen(true)
  }, [])

  const dismiss = useCallback(() => setOpen(false), [])
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss])

  const options = entry?.options
  const undo = open ? options?.undo : undefined
  const paused = hovered || focused
  const counting = open && remaining.current.ms !== undefined && !paused

  useEffect(() => {
    if (!counting || !entry) return
    const started = Date.now()
    const timer = window.setTimeout(() => setOpen(false), remaining.current.ms)
    return () => {
      window.clearTimeout(timer)
      const left = remaining.current
      // A replacement has already reset the clock for the next notice.
      if (left.id === entry.id && left.ms !== undefined) left.ms = Math.max(0, left.ms - (Date.now() - started))
    }
  }, [counting, entry])

  useEscapeFallback(dismiss, open)

  useEffect(() => {
    if (!undo) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.shiftKey || event.altKey) return
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
      // Text fields keep their own undo.
      if (isEditable(event.target)) return
      event.preventDefault()
      undo()
      setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [undo])

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false)
  }

  // The leaving notice still takes clicks while it animates out, and its action must not run twice.
  const run = (handler: () => void) => () => {
    if (!open) return
    handler()
    setOpen(false)
  }

  const action: NoticeAction | undefined = options?.undo
    ? { label: t('shared.actions.undo'), onClick: run(options.undo) }
    : options?.action && { label: options.action.label, onClick: run(options.action.onClick) }

  const notice =
    exit.present && entry && options ? (
      <div
        key={entry.id}
        className="ynt-notice-provider-item"
        data-leaving={exit.leaving || undefined}
        onAnimationEnd={(event) => {
          if (!exit.leaving || event.target !== event.currentTarget) return
          exit.end()
          setHovered(false)
          setFocused(false)
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={onBlur}
      >
        <Notice
          type={options.type}
          action={action}
          onDismiss={options.type === 'loading' ? undefined : dismiss}
          countdown={action ? durationOf(options) : undefined}
          paused={paused}
          className="ynt-notice-provider-notice"
        >
          {options.message}
        </Notice>
      </div>
    ) : null
  const assertive = options?.type === 'error'

  return (
    <NoticeContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ynt-notice-provider-viewport">
          <div role="status" className="ynt-notice-provider-region">
            {!assertive && notice}
          </div>
          <div role="alert" className="ynt-notice-provider-region">
            {assertive && notice}
          </div>
        </div>,
        document.body,
      )}
    </NoticeContext.Provider>
  )
}

export function useNotice(): NoticeApi {
  const api = useContext(NoticeContext)
  if (!api) throw new Error('useNotice must be used inside a NoticeProvider')
  return api
}
